// DevItemTable - §4-2 "개발품별 진행현황" 표.
//
// Dashboard 와 개발품 화면이 같은 컴포넌트를 쓴다 (결정 S-2). 개발품을
// 특정하지 않고는 상세화면을 열 수 없으므로, NAV 로 들어온 사용자에게도
// 같은 표를 선택 목록으로 보여준다.
//
// 키보드 진입점은 품명 셀의 링크 버튼 하나뿐이다. 행 전체를 tabIndex 로
// 만들면 32행 테이블에서 탭이 32번 걸린다 (디자인시스템 §10-19).
// 행 클릭도 하지 않는다 - §4-⑦ 이 "품명 클릭"으로 명시했고 인라인 편집과
// 충돌한다 (결정 S-5).
import { STATUS_RANK, StatusBadge, StatusCell, StatusDot } from "@/components/StatusIndicator";
import { THead, Table, TableScroll, Td, Th, Tr, TrGroup } from "@/components/Table";
import { Button, DDayChip, ProgressBar } from "@/components/ui";
import type { DashboardItem, DashboardSort } from "@/lib/api";
import { monthDay } from "@/lib/dates";
import { EMPTY } from "@/lib/format";
import { STAGE_COLUMNS, type StatusCode, statusKey } from "@/lib/status";
import { useIsMobile } from "@/lib/useViewport";

const COL_COUNT = 13;

/** SOP 표기는 `MM/DD` (§4-2). 연도는 D-Day 칩이 말한다. */
const sopCell = (iso: string): string => monthDay(iso) || EMPTY;

export interface DevItemTableProps {
  items: readonly DashboardItem[];
  sort: DashboardSort;
  order: "asc" | "desc";
  onSort: (col: DashboardSort) => void;
  onOpenItem: (id: string) => void;
  /**
   * 기본 정렬일 때만 상태 그룹 헤더를 그린다. 사용자가 열을 클릭해 정렬을
   * 바꾸면 그룹이 사라지고 단일 목록이 된다 (디자인 가이드 §2).
   */
  grouped?: boolean;
}

const dirOf = (
  col: DashboardSort,
  sort: DashboardSort,
  order: "asc" | "desc"
): "asc" | "desc" | null => (sort === col ? order : null);

export const DevItemTable = ({
  items,
  sort,
  order,
  onSort,
  onOpenItem,
  grouped = true,
}: DevItemTableProps) => {
  const mobile = useIsMobile();
  // 12열 횡스크롤은 §20 "10초 파악" 과 상충하므로 좁은 화면에서는 카드 5항목
  // 으로 접는다 (결정 S-13).
  if (mobile) return <DevItemCards items={items} onOpenItem={onOpenItem} />;

  const showGroups = grouped && sort === "default";
  let lastStatus: StatusCode | null = null;

  return (
    <TableScroll>
      <Table label="개발품별 진행현황">
        <THead>
          <tr>
            <Th width={90} sort={dirOf("customer", sort, order)} onSort={() => onSort("customer")}>
              고객사
            </Th>
            <Th sort={dirOf("part_name", sort, order)} onSort={() => onSort("part_name")}>
              품명
            </Th>
            <Th width={80} sort={dirOf("staff", sort, order)} onSort={() => onSort("staff")}>
              담당자
            </Th>
            <Th width={76} num sort={dirOf("sop", sort, order)} onSort={() => onSort("sop")}>
              SOP
            </Th>
            <Th width={72} num>
              D-Day
            </Th>
            <Th width={100}>현재단계</Th>
            {STAGE_COLUMNS.map((c) => (
              <Th key={c.key} width={40} align="center">
                {c.label}
              </Th>
            ))}
            <Th
              width={72}
              num
              sort={dirOf("progress", sort, order)}
              onSort={() => onSort("progress")}
            >
              진행률
            </Th>
            <Th width={84} sort={dirOf("status", sort, order)} onSort={() => onSort("status")}>
              상태
            </Th>
          </tr>
        </THead>
        <tbody>
          {items.map((it) => {
            const head = showGroups && it.status !== lastStatus;
            if (head) lastStatus = it.status;
            return (
              <DevItemRowGroup
                key={it.id}
                item={it}
                groupHead={
                  head
                    ? {
                        status: it.status,
                        count: items.filter((x) => x.status === it.status).length,
                      }
                    : null
                }
                onOpenItem={onOpenItem}
              />
            );
          })}
        </tbody>
      </Table>
    </TableScroll>
  );
};

const DevItemRowGroup = ({
  item,
  groupHead,
  onOpenItem,
}: {
  item: DashboardItem;
  groupHead: { status: StatusCode; count: number } | null;
  onOpenItem: (id: string) => void;
}) => {
  const late = item.status === "red";
  const key = statusKey(item.status);
  return (
    <>
      {/* 빈 그룹 헤더는 존재하지 않는다 - 행이 있을 때만 헤더가 붙는다
          (디자인시스템 §10-17). 지연 0건인 날에는 정상 그룹이 맨 위로 온다. */}
      {groupHead && (
        <TrGroup
          label={rankLabel(groupHead.status)}
          count={groupHead.count}
          colSpan={COL_COUNT}
          tone={groupHead.status === "red" ? "late" : "default"}
        />
      )}
      <Tr tone={late ? "late" : "default"}>
        <Td>{item.customerName || EMPTY}</Td>
        <Td>
          <Button variant="link" size="sm" onClick={() => onOpenItem(item.id)}>
            {item.partName}
          </Button>
        </Td>
        <Td>{item.staffName || EMPTY}</Td>
        <Td num>{sopCell(item.sopDate)}</Td>
        <Td num>{item.dDay === null ? EMPTY : <DDayChip days={item.dDay} />}</Td>
        <Td>{item.currentStage?.label ?? EMPTY}</Td>
        {STAGE_COLUMNS.map((c) => {
          const st = item.stageStatus[c.key];
          return (
            <Td key={c.key} align="center">
              {st ? <StatusCell status={statusKey(st)} column={c.label} /> : EMPTY}
            </Td>
          );
        })}
        <Td num>
          <ProgressBar value={item.progress} />
        </Td>
        <Td>
          <StatusBadge status={key} size="sm" />
        </Td>
      </Tr>
    </>
  );
};

/** 그룹 헤더 라벨. 정렬 순위(지연 0 -> 완료 4)가 곧 표시 순서다. */
const rankLabel = (status: StatusCode): string => {
  const k = statusKey(status);
  const order = STATUS_RANK[k];
  return order === 0
    ? "지연"
    : order === 1
      ? "일정주의"
      : order === 2
        ? "정상"
        : k === "idle"
          ? "미착수"
          : "완료";
};

/** 모바일 카드. 고객사·품명·상태·진행률·SOP 5항목만 (결정 S-13). */
const DevItemCards = ({
  items,
  onOpenItem,
}: {
  items: readonly DashboardItem[];
  onOpenItem: (id: string) => void;
}) => (
  <ul className="flex list-none flex-col gap-s3 p-0">
    {items.map((it) => (
      <li
        key={it.id}
        className="flex flex-col gap-s2 rounded-card p-s4"
        style={{
          background: it.status === "red" ? "var(--row-late-bg)" : "var(--surface)",
          boxShadow:
            it.status === "red"
              ? "inset 3px 0 0 0 var(--state-late), var(--shadow-raised)"
              : "var(--shadow-raised)",
        }}
      >
        <span className="flex items-center justify-between gap-s3">
          <span className="flex items-baseline gap-s2">
            <span className="text-caption" style={{ color: "var(--text-tertiary)" }}>
              {it.customerName || EMPTY}
            </span>
            <Button variant="link" size="md" onClick={() => onOpenItem(it.id)}>
              {it.partName}
            </Button>
          </span>
          <StatusBadge status={statusKey(it.status)} size="sm" />
        </span>
        <span className="flex items-center justify-between gap-s4">
          <span
            className="flex items-center gap-s2 text-caption tabular-nums"
            data-num=""
            style={{ color: "var(--text-secondary)" }}
          >
            <StatusDot status={statusKey(it.status)} size="row" labelled />
            SOP {sopCell(it.sopDate)}
            {it.dDay !== null && <DDayChip days={it.dDay} />}
          </span>
          <span className="w-[6rem]">
            <ProgressBar value={it.progress} />
          </span>
        </span>
      </li>
    ))}
  </ul>
);
